<!-- src/lib/components/GalleryView.svelte -->
<script lang="ts">
  import { pmStore } from '../stores/pm.svelte.ts';

  interface Props {
    boardId: string;
    onOpenCard: (id: string) => void;
  }
  let { boardId, onOpenCard }: Props = $props();

  let filterQuery    = $state('');
  let filterPriority = $state<string | null>(null);

  const PRIORITY_COLORS: Record<string, string> = {
    urgent: 'oklch(0.62 0.26 25)',
    high:   'oklch(0.78 0.16 85)',
    medium: 'oklch(0.58 0.22 265)',
    low:    'rgba(255,255,255,0.3)',
    none:   'rgba(255,255,255,0.08)',
  };

  const detail = $derived(pmStore.boardDetails[boardId]);

  const allCards = $derived((() => {
    if (!detail) return [];
    return detail.columns.flatMap(col =>
      col.cards.map(card => ({ ...card, columnName: col.name }))
    );
  })());

  const filtered = $derived((() => {
    return allCards.filter(card => {
      if (filterPriority && card.priority !== filterPriority) return false;
      if (filterQuery.trim()) {
        const q = filterQuery.toLowerCase();
        return card.title.toLowerCase().includes(q) ||
               (card.description?.toLowerCase().includes(q) ?? false);
      }
      return true;
    });
  })());

  const priorities = [
    { value: 'urgent', label: 'U', color: 'oklch(0.62 0.26 25)' },
    { value: 'high',   label: 'H', color: 'oklch(0.78 0.16 85)' },
    { value: 'medium', label: 'M', color: 'oklch(0.58 0.22 265)' },
    { value: 'low',    label: 'L', color: 'rgba(255,255,255,0.3)' },
  ];

  function formatDate(iso: string | null | undefined): string {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return iso.slice(0, 10);
    }
  }
</script>

<div class="gallery-container">
  <div class="gallery-toolbar">
    <input
      class="search-input"
      type="text"
      placeholder="Filter cards…"
      bind:value={filterQuery}
    />
    <div class="priority-pills">
      {#each priorities as p}
        <button
          class="priority-pill"
          class:active={filterPriority === p.value}
          style="--pill-color: {p.color};"
          onclick={() => { filterPriority = filterPriority === p.value ? null : p.value; }}
          title={p.value}
        >
          {p.value}
        </button>
      {/each}
      {#if filterPriority}
        <button class="clear-filter" onclick={() => { filterPriority = null; }}>✕</button>
      {/if}
    </div>
    <span class="card-count">{filtered.length} card{filtered.length === 1 ? '' : 's'}</span>
  </div>

  <div class="gallery-scroll">
    {#if filtered.length === 0}
      <div class="empty-state">No cards yet</div>
    {:else}
      <div class="gallery-grid">
        {#each filtered as card}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div class="card-tile" onclick={() => onOpenCard(card.id)}>
            <div
              class="tile-priority-bar"
              style="background: {PRIORITY_COLORS[card.priority] ?? PRIORITY_COLORS.none};"
            ></div>
            <div class="tile-body">
              <div class="tile-header">
                <span class="tile-title">{card.title}</span>
                <span class="tile-column-chip">{card.columnName}</span>
              </div>
              {#if card.description}
                <p class="tile-desc">{card.description}</p>
              {/if}
              <div class="tile-footer">
                <span
                  class="priority-dot"
                  style="background: {PRIORITY_COLORS[card.priority] ?? PRIORITY_COLORS.none};"
                  title={card.priority}
                ></span>
                <span>{card.priority !== 'none' ? card.priority : 'no priority'}</span>
                {#if card.agent_id}
                  <span class="agent-badge" title={card.agent_id}>⊙ agent</span>
                {/if}
                {#if card.due_date}
                  <span class="due-badge">📅 {formatDate(card.due_date)}</span>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
</div>

<style>
  .gallery-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background: var(--bg-base);
  }

  /* ── Toolbar ─────────────────────────────────────── */
  .gallery-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 1rem;
    border-bottom: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    flex-shrink: 0;
    background: var(--bg-elevated, oklch(17% 0.015 270));
  }

  .search-input {
    background: var(--bg-base, oklch(14% 0.01 270));
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 5px;
    color: var(--text-primary, oklch(92% 0.01 270));
    font-size: 0.78rem;
    padding: 0.3rem 0.625rem;
    outline: none;
    width: 220px;
    font-family: inherit;
    transition: border-color 120ms ease;
  }

  .search-input::placeholder {
    color: var(--text-muted, oklch(55% 0.01 270));
  }

  .search-input:focus {
    border-color: oklch(0.74 0.28 310 / 0.39);
  }

  .priority-pills {
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .priority-pill {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.4);
    border-radius: 4px;
    padding: 0.2rem 0.6rem;
    font-size: 0.7rem;
    cursor: pointer;
    text-transform: capitalize;
    transition: background-color 100ms ease, color 100ms ease, border-color 100ms ease;
    font-family: inherit;
  }

  .priority-pill:hover {
    background: rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.7);
  }

  .priority-pill.active {
    background: color-mix(in srgb, var(--pill-color) 15%, transparent);
    border-color: var(--pill-color);
    color: var(--pill-color);
  }

  .clear-filter {
    background: transparent;
    border: none;
    color: rgba(255,255,255,0.3);
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0.2rem 0.3rem;
    transition: color 100ms ease;
    font-family: inherit;
  }

  .clear-filter:hover { color: rgba(255,255,255,0.7); }

  .card-count {
    font-size: 0.72rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    font-variant-numeric: tabular-nums;
    margin-left: auto;
  }

  /* ── Scroll + Grid ───────────────────────────────── */
  .gallery-scroll {
    flex: 1;
    overflow-y: auto;
  }

  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--card-gap, 0.75rem);
    padding: 1.25rem;
  }

  /* ── Card tile ───────────────────────────────────── */
  .card-tile {
    background: var(--bg-elevated, oklch(17% 0.015 270));
    border: 1px solid var(--border-faint, rgba(255,255,255,0.06));
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    transition: transform 100ms ease;
    display: flex;
    flex-direction: column;
  }

  .card-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  }

  .tile-priority-bar {
    height: 3px;
    flex-shrink: 0;
  }

  .tile-body {
    padding: 0.75rem;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  .tile-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .tile-column-chip {
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255,255,255,0.35);
    background: rgba(255,255,255,0.06);
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    flex-shrink: 0;
    white-space: nowrap;
  }

  .tile-title {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary, oklch(92% 0.01 270));
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    flex: 1;
  }

  .tile-desc {
    font-size: 0.75rem;
    color: var(--text-muted, oklch(55% 0.01 270));
    line-height: 1.45;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: 0.125rem;
    margin-bottom: 0;
  }

  .tile-footer {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: auto;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(255,255,255,0.04);
    font-size: 0.68rem;
    color: rgba(255,255,255,0.3);
  }

  .priority-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .agent-badge {
    color: oklch(0.78 0.26 310 / 0.58);
    font-size: 0.65rem;
  }

  .due-badge {
    font-size: 0.65rem;
    color: rgba(255,255,255,0.25);
  }

  /* ── Empty state ─────────────────────────────────── */
  .empty-state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255,255,255,0.2);
    font-size: 0.875rem;
    padding: 4rem;
  }
</style>
